import { useParams } from 'react-router-dom';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useGetOperationPartners } from 'modules/transactions/hooks/useGetOperationPartners';
import { useGetOperationDocuments } from 'modules/transactions/hooks/useGetOperationDocuments';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { setModal } from 'modules/_shared/redux/modalActions';
import {
  TaxIncomeDocumentShareholdersType,
  generateTaxIncomeDocument,
} from 'modules/partners/components/PartnerAside/utils/generateTaxIncomeDocument';
import { DividendDocumentModalProvider } from 'modules/transactions/components/DividendDocumentModal/context/DividendDocumentModalContext';
import { NotifyPartnersDividendsModalProvider } from 'modules/transactions/modals/NotifyPartnersDividendsModal/context/NotifyPartnersDividendsModalContext';
import DividendDocumentModal from 'modules/transactions/components/DividendDocumentModal';
import NotifyPartnersDividendsModal from 'modules/transactions/modals/NotifyPartnersDividendsModal';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { generateSEPAFile } from 'modules/transactions/utils/bankSEPAGenerator';
import { OperationDetailsPageContextType } from '../types/OperationDetailsPageContextType';
import { operationDetailsPageInitValue } from '../constants/operationDetailsPageInitValue';
import { useGetOperation } from '../hooks/useGetOperation';

const OperationDetailsPageContext =
  createContext<OperationDetailsPageContextType>(operationDetailsPageInitValue);

export const OperationDetailsPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();

  const { societyId, transactionId: operationId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useGetActualSociety(societyId);
  useGetMenu();

  const { data: operationData, isLoading: isLoadingOperation } =
    useGetOperation({ operationId });

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    actions: { fetchDocuments },
  } = useGetOperationDocuments({
    operationId,
  });

  const { data: partnersData, isLoading: isLoadingParticipants } =
    useGetOperationPartners({
      operationId,
      page: currentPage,
    });

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleOpenModal = (
    ModalProvider: FC<{ children: ReactNode; operationId: string | undefined }>,
    Modal: FC
  ): void => {
    if (!operationId) return;

    dispatch(
      setModal(
        <ModalProvider operationId={operationId}>
          <Modal />
        </ModalProvider>
      )
    );
  };

  const handleOpenDividendModal = () =>
    handleOpenModal(DividendDocumentModalProvider, DividendDocumentModal);

  const handleNotifyPartners = () =>
    handleOpenModal(
      NotifyPartnersDividendsModalProvider,
      NotifyPartnersDividendsModal
    );

  const handleGenerateTaxDeclaration = async (
    shareholdersType: TaxIncomeDocumentShareholdersType
  ) => {
    if (!actualSociety || !operationData.operation) return;

    setIsLoading(true);
    try {
      await generateTaxIncomeDocument({
        partner: null,
        society: actualSociety,
        operation: operationData.operation,
        shareholdersType,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateBankDocument = async () => {
    if (!actualSociety || !operationData.operation) return;

    await generateSEPAFile({
      operationId,
      society: actualSociety,
      operationType: operationData.operation.operationType,
    });
  };

  return (
    <OperationDetailsPageContext.Provider
      value={{
        data: {
          operation: operationData.operation,
          partners: partnersData.partners,
          totalPartners: partnersData.totalPartners,
          currentPage,
          rowsPerPage,
          documents: documentsData.documents,
          isOldOperation: operationData.isOldOperation,
          isIndependentOperation: operationData.isIndependentOperation,
          isLoading:
            isLoadingOperation ||
            isDocumentsLoading ||
            isLoadingParticipants ||
            isLoading,
        },
        actions: {
          fetchDocuments,
          handleChangePage,
          handleChangeRowsPerPage,
          handleOpenDividendModal,
          handleNotifyPartners,
          handleGenerateTaxDeclaration,
          handleGenerateBankDocument,
        },
      }}
    >
      {children}
    </OperationDetailsPageContext.Provider>
  );
};

export const useOperationDetailsPage = () => {
  const context = useContext(OperationDetailsPageContext);
  if (!context) {
    throw new Error(
      'useOperationDetailsPage must be used within a OperationDetailsPageProvider'
    );
  }
  return context;
};
